import { v1CategoriesHeadlinesPaginatedDetail } from '@on3/api';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi, webUrl } from '@on3/ui-lib/utils/api';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import type { AxiosError } from 'axios';
import {
  CategoryPage,
  type ICategoryPageProps,
} from 'components/Category/CategoryPage';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { format } from 'date-fns';
import type { GetServerSideProps } from 'next';
import { CollectionPageSchema } from 'store/schemas/CollectionPage';

const NetworkHomePage = (props: ICategoryPageProps) => {
  const { category, articles } = props;
  const isCFP = category?.categoryKey === 557924;
  const name = category?.categoryName || '';
  const pageTitle = isCFP ? name || 'College Football Playoff' : undefined;

  const title = isCFP ? name : `${name} News | On3.com`;
  const description = `The latest ${name} News.`;
  const canonical = webUrl + `/category${category?.url}/news/`;

  const schema = new CollectionPageSchema({
    name: pageTitle || name,
    description,
    url: canonical,
    breadcrumb: [
      {
        name,
        item: canonical,
      },
    ],
    about: isCFP
      ? {
          type: 'SportsEvent',
          name,
          sport: 'College Football',
        }
      : {
          type: 'Thing',
          name,
        },
    mainEntity: {
      name: pageTitle || name,
      list:
        articles?.list?.map((article) => ({
          headline: article?.title as string,
          url: webUrl + `/news/${article?.slug}/`,
          image: getAssetUrl(article?.featuredImage),
          author: article?.author?.name,
          datePublished: format(new Date(article?.postDate), 'yyyy-MM-dd'),
        })) || [],
    },
  }).toJSON();

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      schema={schema}
      title={title}
    >
      <CategoryPage {...props} pageTitle={pageTitle} />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { category } = ctx.query;
  const { currentSite } = siteData || {};
  const currentCategory = currentSite?.siteCategories?.find(
    (siteCategory) => siteCategory?.url?.substring(1) === category,
  );

  if (!currentCategory?.categoryKey) {
    console.error({ url: ctx?.req?.url, error: 'category null' });

    return {
      notFound: true,
    };
  }

  try {
    const { page = 1 } = ctx.query;

    const [articles] = await Promise.allSettled([
      v1CategoriesHeadlinesPaginatedDetail(proxyApi, {
        category: currentCategory?.categoryKey,
        page: +page,
      }),
    ]);

    return {
      props: {
        pageType: 'home',
        siteData: siteData || [],
        userData: userData || {},
        category: currentCategory,
        articles: getFulfilledValue(articles),
        page,
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err.message);

    return { props: {} };
  }
};

export default withValidUrl(NetworkHomePage);
